import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import "../../App.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Button, Box, Typography, Menu, MenuItem } from "@material-ui/core";
import CustomCheckbox from "../../components/checkboxRenderer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LicenseManager } from "ag-grid-enterprise";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import fnOpenToast from "../../components/toast";
import { Link as ReactLink, useParams } from "react-router-dom";
import SubMenu from "./submenu";
import { camelCaseToTitle } from "../../utils/camelCaseToTitle";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RedoIcon from "@material-ui/icons/Redo";
import AddMetadataDialog from "../../components/addMetadataDialog";
import AddMetadataCollectionDialog from "../../components/addMetadataCollectionDialog";
import {
    createMetadata,
    getMetadata,
    updateMetadata,
    getCountriesCurrencies,
    getCollections,
    createMetadataCollection,
} from "../../api";
import withMultiSelectGridCountryCheckbox from "../../hocs/withMultiSelectGridCountryCheckbox";
import MultiSelectCheckbox from "../../components/multiSelectCheckbox";

toast.configure();
LicenseManager.setLicenseKey(
    "Using_this_AG_Grid_Enterprise_key_( AG-044136 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Lucid USA, Inc )_is_granted_a_( Single Application )_Developer_License_for_the_application_( V360 )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( V360 )_need_to_be_licensed___( V360 )_has_been_granted_a_Deployment_License_Add-on_for_( 2 )_Production_Environments___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 28 August 2024 )____[v2]_MTcyNDc5OTYwMDAwMA==c89ee173c77380f5ddc3c6af80f11777",
);

const metadataInitialValue = {
    name: {
        value: "",
        hasError: false,
    },
    countries: {
        value: [],
        hasError: false,
    },
    isActive: {
        value: true,
        hasError: false,
    },
};

const metadataCollectionInitialValue = {
    name: {
        value: "",
        hasError: false,
    },
    countries: {
        value: [],
        hasError: false,
    },
    isMultipleSelection: {
        value: false,
        hasError: false,
    },
};

function Metadata() {
    const gridRef = useRef();
    const [gridApi, setGridApi] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [countryOption, setCountryOption] = useState([]);
    const [createMetadataDialogOpen, setCreateMetadataDialogOpen] =
        useState(false);
    const [formData, setFormData] = useState(metadataInitialValue);
    const [
        createMetadataCollectionDialogOpen,
        setCreateMetadataCollectionDialogOpen,
    ] = useState(false);
    const [createCollectionFormData, setCreateCollectionFormData] = useState(
        metadataCollectionInitialValue,
    );
    const [enableEditing, setEnableEditing] = useState(false);
    const [rowsUpdated, setRowsUpdated] = useState(new Map());
    const [collections, setCollections] = useState([]);

    const { country, collection } = useParams();

    const frameworkComponents = {
        checkboxRenderer: CustomCheckbox,
    };

    const columnDefs = [
        {
            headerName: "Name",
            field: "name",
        },
        {
            headerName: "Countries",
            field: "countries",
            cellEditor: withMultiSelectGridCountryCheckbox(MultiSelectCheckbox),
            cellEditorParams: (params) => {
                return {
                    values: countryOption,
                    eGridCell: params.eGridCell,
                };
            },
            width: 300,
            cellRenderer: ({ value }) => {
                if (value?.length === countryOption?.length) {
                    return "All";
                }
                return value.map((item) => item?.label).join(", ");
            },
            valueGetter: ({ data }) => {
                return data.countries.map((country) =>
                    countryOption.find(
                        (option) =>
                            option.value ===
                            (typeof country === "string"
                                ? country
                                : country.value),
                    ),
                );
            },
            valueSetter: ({ data, newValue }) => {
                data.countries = newValue;
            },
            filter: "agSetColumnFilter",
            filterParams: {
                getValue: (params) => {
                    return params.data.countries;
                },
            },
        },
        {
            headerName: "Active",
            field: "isActive",
            cellRenderer: "checkboxRenderer",
        },
    ];

    const defaultColDef = {
        sortable: true,
        filter: true,
        floatingFilter: true,
        editable: enableEditing,
        resizable: true,
    };

    useEffect(() => {
        refreshCollections();
    }, []);

    useEffect(() => {
        getCountriesList();
        getData();
    }, [collection]);

    const refreshCollections = async () => {
        const collectionsList = await getCollections();
        setCollections(collectionsList);
    };

    const onMetadataChange = (e) => {
        const { value, id, name } = e.target;
        setFormData({
            ...formData,
            [id ?? name]: { value: value, hasError: false },
        });
    };

    const onMetadataCollectionChange = (e) => {
        const { value, id, name } = e.target;
        setCreateCollectionFormData({
            ...createCollectionFormData,
            [id ?? name]: { value: value, hasError: false },
        });
    };

    const onGridReady = (params) => {
        setGridApi(params);
    };

    const getData = async () => {
        const metadata = await getMetadata(collection);
        setTableData(metadata);
        await refreshCollections();
    };

    const getCountriesList = async () => {
        const countriesCurrencies = await getCountriesCurrencies();
        const countryOptionArr = [];
        countriesCurrencies.map((item) => {
            const countryOption = {
                label: `${item.countryISO2Code} - ${item.countryName}`,
                value: item.countryISO2Code,
            };
            countryOptionArr.push(countryOption);
        });
        const allCountries = {
            value: countryOptionArr.map((c) => c.value),
            hasError: false,
        };

        setCountryOption(countryOptionArr);
        setFormData((form) => ({ ...form, countries: allCountries }));
        setCreateCollectionFormData((form) => ({
            ...form,
            countries: allCountries,
        }));
    };

    const handleUpdate = async (editedRows) => {
        const updateRequest = {
            updates: editedRows.map((row) => ({
                ...row,
                countries: row.countries.map((c) => c?.value ?? c),
            })),
        };
        const confirm = window.confirm(
            "Are you sure, you want to update this row ?",
        );
        if (!confirm) {
            return;
        }
        setEnableEditing(false);
        try {
            const errorOcurred = await updateMetadata(
                collection,
                updateRequest,
            );
            errorOcurred[0]?.status === "fulfilled"
                ? fnOpenToast("Metadata Updated Successfully!", "success")
                : notifyError(errorOcurred?.message || "Something went wrong");
        } catch (error) {
            notifyError(error.message);
        }

        await getData();
    };

    const handleActionClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleActionClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleClickOpen = (dialog) => {
        switch (dialog) {
            case "metadata": {
                setCreateMetadataDialogOpen(true);
                break;
            }
            case "metadataCollection": {
                setCreateMetadataCollectionDialogOpen(true);
                break;
            }
        }
    };

    const handleClose = (dialog) => {
        const allCountries = {
            value: countryOption.map((c) => c.value),
            hasError: false,
        };
        switch (dialog) {
            case "metadata": {
                setCreateMetadataDialogOpen(false);
                setFormData({
                    ...metadataInitialValue,
                    countries: allCountries,
                });
                break;
            }
            case "metadataCollection": {
                setCreateMetadataCollectionDialogOpen(false);
                setCreateCollectionFormData({
                    ...metadataCollectionInitialValue,
                    countries: allCountries,
                });
            }
        }
    };

    const handleSaveEdited = () => {
        const editedRows = Array.from(rowsUpdated.values());
        handleUpdate(editedRows);
        setRowsUpdated(new Map());
    };

    const handleMetadataFormSubmit = async (event) => {
        event.preventDefault();
        const request = constructRequest(formData);

        try {
            await createMetadata(collection, request);
            handleClose("metadata");
            await getData();
            fnOpenToast("Metadata Added Successfully", "success");
        } catch (e) {
            fnOpenToast(e.message, "error");
        }
    };

    const handleMetadataCollectionFormSubmit = async (event) => {
        event.preventDefault();
        const request = constructRequest(createCollectionFormData);

        try {
            await createMetadataCollection({ ...request, isActive: true });
            handleClose("metadataCollection");
            await getData();
            fnOpenToast("Metadata Collection Added Successfully", "success");
        } catch (e) {
            fnOpenToast(e.message, "error");
        }
    };

    const handleCellValueChanged = (event) => {
        if (!event?.data?.name) {
            fnOpenToast("Name field is required", "error");
            return;
        } else {
            setRowsUpdated(rowsUpdated.set(event?.data?._id, event?.data));
        }
    };

    const constructRequest = (data) => {
        const request = {
            name: data.name.value,
            countries: data.countries.value || [],
            isActive: data.isActive?.value,
            isMultipleSelection: data?.isMultipleSelection?.value,
        };
        return request;
    };

    return (
        <>
            <div>
                <div style={{ margin: "10px 20px" }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <ReactLink
                            color="inherit"
                            to="/"
                            className="breadcrumb-link"
                        >
                            Dashboard
                        </ReactLink>
                        <ReactLink
                            color="inherit"
                            to={`/vehiclesalesfees/${country}`}
                            className="breadcrumb-link"
                        >
                            Fee Administration
                        </ReactLink>
                        <Typography color="textPrimary">{`${camelCaseToTitle(collection)} - Edit Metadata`}</Typography>
                    </Breadcrumbs>
                </div>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    style={{ margin: "10px 20px" }}
                >
                    <div>
                        <Typography
                            variant="h5"
                            component={"h5"}
                            style={{
                                fontFamily: "GTAmericaMedium",
                                fontSize: "24px",
                                lineHeight: "120%",
                                fontWeight: "500",
                            }}
                        >
                            {`${camelCaseToTitle(collection)} - Edit Metadata`}
                        </Typography>
                    </div>
                    <>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <SubMenu
                                country={country}
                                collections={collections}
                            />
                            <Button
                                variant="outlined"
                                color="primary"
                                aria-controls="action-menu"
                                aria-haspopup="true"
                                onClick={handleActionClick}
                            >
                                Actions <ExpandMoreIcon />
                            </Button>
                            <Menu
                                id="action-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleActionClose}
                            >
                                <MenuItem
                                    onClick={() =>
                                        handleClickOpen("metadataCollection")
                                    }
                                >
                                    <AddCircleOutlineIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Add Metadata Collection
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleClickOpen("metadata")}
                                >
                                    <AddCircleOutlineIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    {`Add ${camelCaseToTitle(collection)}`}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => setEnableEditing(true)}
                                >
                                    <EditIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Enable Editing
                                </MenuItem>
                                <MenuItem onClick={handleSaveEdited}>
                                    <CheckCircleOutlineIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Save Changes
                                </MenuItem>
                                <MenuItem
                                    onClick={() => setEnableEditing(false)}
                                    disabled={!enableEditing}
                                >
                                    <RedoIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Cancel Changes
                                </MenuItem>
                            </Menu>
                        </Box>
                    </>
                </Box>
                <table
                    style={{
                        margin: "0 20px",
                        width: "calc(100% - 40px)",
                        height: "calc(100vh - 170px)",
                    }}
                >
                    <tbody>
                        <tr>
                            <td style={{ border: "1px solid #babfc7" }}>
                                <div
                                    className="ag-theme-alpine"
                                    style={{
                                        height: "100%",
                                        width: "100%",
                                    }}
                                >
                                    <AgGridReact
                                        rowData={tableData}
                                        columnDefs={columnDefs}
                                        defaultColDef={defaultColDef}
                                        onGridReady={onGridReady}
                                        ref={gridRef}
                                        onCellValueChanged={
                                            handleCellValueChanged
                                        }
                                        enableCellChangeFlash={true}
                                        frameworkComponents={
                                            frameworkComponents
                                        }
                                    />
                                </div>
                                <AddMetadataDialog
                                    open={createMetadataDialogOpen}
                                    handleClose={() => handleClose("metadata")}
                                    data={formData}
                                    onChange={onMetadataChange}
                                    handleFormSubmit={handleMetadataFormSubmit}
                                    countryOption={countryOption}
                                    collection={collection}
                                />
                                <AddMetadataCollectionDialog
                                    open={createMetadataCollectionDialogOpen}
                                    handleClose={() =>
                                        handleClose("metadataCollection")
                                    }
                                    data={createCollectionFormData}
                                    onChange={onMetadataCollectionChange}
                                    handleFormSubmit={
                                        handleMetadataCollectionFormSubmit
                                    }
                                    countryOption={countryOption}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default Metadata;
